import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from './ui/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import GridPagination from './GridPagination';
import useClientPagination from '../hooks/useClientPagination';
import {
  MapPin,
  Clock,
  Navigation,
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  FileText,
  TrendingUp,
  Filter,
  Wrench
} from 'lucide-react';

const RegistroViagemPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [viagens, setViagens] = useState([]);
  const [viagensFiltradas, setViagensFiltradas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todas');
  const [filtroFuncionario, setFiltroFuncionario] = useState('todos');
  const [funcionarios, setFuncionarios] = useState([]);
  
  // Estatísticas
  const [stats, setStats] = useState({
    totalViagens: 0,
    totalKm: 0,
    pendentesAprovacao: 0,
    viagensHoje: 0
  });
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Paginação client-side para a grid de viagens
  const { paginated: viagensPaginadas, pagination, setPage } = useClientPagination(viagensFiltradas, 10);

  // Carregar viagens
  const carregarViagens = async () => {
    setCarregando(true);
    try {
      const token = localStorage.getItem('token');
      
      let url = `${API_URL}/api/viagens`;
      
      // Se não for admin, filtrar apenas viagens do próprio funcionário
      if (!user.is_admin && user.employee_id) {
        url += `?funcionario_id=${user.employee_id}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setViagens(response.data);
      setViagensFiltradas(response.data);
      
      // Calcular estatísticas
      calcularEstatisticas(response.data);
      
      // Extrair lista de funcionários únicos
      const funcsUnicos = [...new Set(response.data.map(v => v.funcionario_nome))].filter(Boolean);
      setFuncionarios(funcsUnicos);
      
    } catch (error) {
      console.error('Erro ao carregar viagens:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar viagens",
        variant: "destructive"
      });
    } finally {
      setCarregando(false);
    }
  };

  // Calcular estatísticas
  const calcularEstatisticas = (viagensData) => {
    const hoje = new Date().toISOString().split('T')[0];
    
    const stats = {
      totalViagens: viagensData.length,
      totalKm: viagensData.reduce((sum, v) => sum + (v.km_rodados || 0), 0),
      pendentesAprovacao: viagensData.filter(v => v.status === 'finalizada').length,
      viagensHoje: viagensData.filter(v => v.data_hora_inicio?.startsWith(hoje)).length
    };
    
    setStats(stats);
  };

  // Aplicar filtros
  useEffect(() => {
    let filtered = viagens;
    
    // Filtro por status
    if (filtroStatus !== 'todas') {
      filtered = filtered.filter(v => v.status === filtroStatus);
    }
    
    // Filtro por funcionário
    if (filtroFuncionario !== 'todos') {
      filtered = filtered.filter(v => v.funcionario_nome === filtroFuncionario);
    }
    
    setViagensFiltradas(filtered);
    calcularEstatisticas(filtered);
  }, [filtroStatus, filtroFuncionario, viagens]);

  // Aprovar viagem
  const aprovarViagem = async (viagemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/viagens/${viagemId}/aprovar`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Viagem Aprovada",
        description: "Viagem aprovada com sucesso",
      });
      
      carregarViagens();
    } catch (error) {
      console.error('Erro ao aprovar viagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao aprovar viagem",
        variant: "destructive"
      });
    }
  };

  // Deletar viagem
  const deletarViagem = async (viagemId) => {
    if (!confirm('Deseja realmente deletar esta viagem?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/viagens/${viagemId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Viagem Deletada",
        description: "Viagem removida com sucesso",
      });
      
      carregarViagens();
    } catch (error) {
      console.error('Erro ao deletar viagem:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || "Erro ao deletar viagem",
        variant: "destructive"
      });
    }
  };

  useEffect(() => {
    carregarViagens();
  }, []);

  // Formatar data
  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formatar tempo
  const formatarTempo = (minutos) => {
    if (!minutos) return '-';
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return `${horas}h ${mins}min`;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📍 Registro de Viagem</h1>
          <p className="text-gray-600 mt-1">Controle de quilometragem e viagens externas</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => {
              const token = localStorage.getItem('token');
              window.open(`${API_URL}/api/viagens/export/excel?Authorization=${token}`, '_blank');
            }}
            variant="outline"
            className="bg-green-50 hover:bg-green-100 border-green-300"
          >
            <FileText className="h-4 w-4 mr-2" />
            Exportar Excel
          </Button>
          <Button onClick={carregarViagens} variant="outline">
            Atualizar
          </Button>
          <Button
            onClick={() => navigate('/registro-viagem-admin')}
            variant="outline"
            className="text-gray-600"
            title="Ferramenta de emergência para encerrar viagens travadas"
          >
            <Wrench className="h-4 w-4 mr-2" />
            Ferramentas
          </Button>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total de Viagens</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalViagens}</p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total KM</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalKm.toFixed(1)}</p>
              </div>
              <Navigation className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendentesAprovacao}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Viagens Hoje</p>
                <p className="text-2xl font-bold text-gray-900">{stats.viagensHoje}</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filtros</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="todas">Todas</option>
                <option value="em_andamento">Em Andamento</option>
                <option value="pausada">Pausada</option>
                <option value="finalizada">Finalizada</option>
                <option value="aprovada">Aprovada</option>
              </select>
            </div>

            {user.is_admin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Funcionário
                </label>
                <select
                  value={filtroFuncionario}
                  onChange={(e) => setFiltroFuncionario(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="todos">Todos</option>
                  {funcionarios.map((func) => (
                    <option key={func} value={func}>{func}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista de Viagens */}
      <Card>
        <CardHeader>
          <CardTitle>Viagens Registradas</CardTitle>
        </CardHeader>
        <CardContent>
          {carregando ? (
            <div className="text-center py-12">
              <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-600 mt-4">Carregando viagens...</p>
            </div>
          ) : viagensFiltradas.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Nenhuma viagem encontrada</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Data/Hora</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Funcionário</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">KM</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Tempo</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Visitas</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Alertas</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                    {user.is_admin && (
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {viagensPaginadas.map((viagem) => (
                    <tr key={viagem.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatarData(viagem.data_hora_inicio)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {viagem.funcionario_nome || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-blue-600">
                        {viagem.km_rodados?.toFixed(1)} KM
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatarTempo(viagem.tempo_total_minutos)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {viagem.visitas?.length || 0} visita{viagem.visitas?.length !== 1 ? 's' : ''}
                        {viagem.observacoes?.length > 0 && (
                          <div className="text-xs text-blue-600 mt-1">
                            {viagem.observacoes.length} obs.
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {viagem.paradas_longas_nao_registradas > 0 && (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            {viagem.paradas_longas_nao_registradas} alerta{viagem.paradas_longas_nao_registradas !== 1 ? 's' : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          viagem.status === 'aprovada' ? 'bg-green-100 text-green-700' :
                          viagem.status === 'finalizada' ? 'bg-blue-100 text-blue-700' :
                          viagem.status === 'em_andamento' ? 'bg-yellow-100 text-yellow-700' :
                          viagem.status === 'pausada' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {viagem.status === 'aprovada' ? 'Aprovada' :
                           viagem.status === 'finalizada' ? 'Finalizada' :
                           viagem.status === 'em_andamento' ? 'Em Andamento' :
                           viagem.status === 'pausada' ? 'Pausada' : viagem.status}
                        </span>
                      </td>
                      {user.is_admin && (
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col space-y-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/historico-viagens/${viagem.id}`)}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Detalhes
                            </Button>
                            {viagem.status === 'finalizada' && (
                              <Button
                                size="sm"
                                onClick={() => aprovarViagem(viagem.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Aprovar
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deletarViagem(viagem.id)}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Deletar
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <GridPagination pagination={pagination} onPageChange={setPage} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistroViagemPage;
